import { apiRequest, apiRoutes } from '../../../../api';
/**
 * GeoScopeAnalysis.js — تحلیل جغرافیایی ترجیحات محیطی
 *
 * Third tab inside Step5_GeoScope.
 *
 * Flow (mirrors GeoQuery.js from MainPage, adapted for the wizard):
 *  1. Fetch GeoFeatures where hcm_type=GEOSPATIAL (active, linked to HCMFeature)
 *  2. User picks a feature (e.g. "دسترسی به ایستگاه اتوبوس")
 *  3. Load its POIs from poi_types → render as markers on a Leaflet map
 *  4. User sets buffer radius (metres) → draw a circle around every POI
 *  5. User clicks "تأیید و ادامه" → the union of all circles for that feature
 *     becomes one approved boundary layer (stored in approvedLayers[])
 *  6. User picks another feature → repeat steps 2–5
 *  7. When done, the component calls onBoundariesChange(multiPolygonGeoJSON)
 *     with the INTERSECTION of all approved layers (where all conditions overlap)
 *
 * The final MultiPolygon is an approximate intersection: we compute it client-side
 * by checking which circle rings overlap all other layers using the same
 * point-in-polygon approach used throughout the rest of the wizard.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, useMap } from 'react-leaflet';import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { createCirclePolygon } from '../../../../utils/distanceUtils';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
    iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
    shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const PALETTE = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00acc1', '#f4511e', '#039be5'];
// ── tiny ray-cast PiP (lng,lat coords) ───────────────────────────────────────
function pip(point, ring) {
    const [px, py] = point;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi) / (yj - yi) + xi))
            inside = !inside;
    }
    return inside;
}

// ── Extract {lat, lng} from a POI (GeoJSON Feature or plain object) ──────────
function poiCoords(poi) {
    if (!poi) return null;

    if (poi.geometry) {
        const { type, coordinates } = poi.geometry;

        // Point: coordinates = [lng, lat]
        if (type === 'Point' && coordinates?.length >= 2) {
            const lng = parseFloat(coordinates[0]);
            const lat = parseFloat(coordinates[1]);
            if (isFinite(lat) && isFinite(lng)) return { lat, lng };
        }

        // MultiPoint: coordinates = [[lng, lat], ...]  — use the first point
        if (type === 'MultiPoint' && coordinates?.length >= 1) {
            const first = coordinates[0];
            if (Array.isArray(first) && first.length >= 2) {
                const lng = parseFloat(first[0]);
                const lat = parseFloat(first[1]);
                if (isFinite(lat) && isFinite(lng)) return { lat, lng };
            }
        }
    }

    // Plain {lat, lon} or {lat, lng}
    const lat1 = poi.lat ?? poi.latitude;
    const lng1 = poi.lon ?? poi.lng ?? poi.longitude;
    if (lat1 != null && lng1 != null) {
        const lat = parseFloat(lat1), lng = parseFloat(lng1);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }

    // Nested location object
    if (poi.location?.lat != null) {
        const lat = parseFloat(poi.location.lat);
        const lng = parseFloat(poi.location.lon ?? poi.location.lng);
        if (isFinite(lat) && isFinite(lng)) return { lat, lng };
    }

    return null;
}

// ── Approved layer polygon renderer ──────────────────────────────────────────
function ApprovedLayerPolygons({ layers }) {
    return layers.map((layer, idx) => {
        const color = PALETTE[idx % PALETTE.length];
        return layer.rings.map((ring, ri) => {
            const leafletCoords = ring.map(([lng, lat]) => [lat, lng]);
            return (
                <Polygon
                    key={`${idx}-${ri}`}
                    positions={leafletCoords}
                    pathOptions={{ color, fillColor: color, fillOpacity: 0.12, weight: 1.5, opacity: 0.6, dashArray: '4 3' }}
                />
            );
        });
    });
}

// ── All POI markers + circles added imperatively so they always appear ────────
function POILayers({ pois, bufferMetres }) {
    const map = useMap();

    useEffect(() => {
        // Always clear and rebuild — even if pois is empty (clears stale layers)
        const layerGroup = L.layerGroup().addTo(map);
        let added = 0;

        pois.slice(0, 300).forEach((poi) => {
            const c = poiCoords(poi);
            if (!c || !isFinite(c.lat) || !isFinite(c.lng)) {
                return;
            }
            const latlng = L.latLng(c.lat, c.lng);
            L.marker(latlng).addTo(layerGroup);
            L.circle(latlng, {
                radius: bufferMetres,
                color: '#3949ab',
                fillColor: '#3949ab',
                fillOpacity: 0.15,
                weight: 2,
            }).addTo(layerGroup);
            added++;
        });

        if (added > 0) {
            const coords = pois
                .map(p => poiCoords(p))
                .filter(c => c && isFinite(c.lat) && isFinite(c.lng));
            const bounds = L.latLngBounds(coords.map(c => [c.lat, c.lng]));
            map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
        }

        return () => { map.removeLayer(layerGroup); };
    }, [pois, bufferMetres, map]); // eslint-disable-line react-hooks/exhaustive-deps

    return null;
}

// ── POI map ───────────────────────────────────────────────────────────────────
function POIMap({ pois, bufferMetres, approvedLayers }) {
    return (
        <MapContainer
            center={[35.6892, 51.389]}
            zoom={12}
            className="gsa-map"
        >
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
            />
            <ApprovedLayerPolygons layers={approvedLayers} />
            <POILayers pois={pois} bufferMetres={bufferMetres} />
        </MapContainer>
    );
}
async function loadPOIs(poiTypeIds) {
    if (!poiTypeIds?.length) return [];
    const all = [];
    for (const typeId of poiTypeIds) {
        try {
            const r = await apiRequest(
                apiRoutes.poi.data,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
                    params: { poi_type_id: typeId }
                }
            );
            if (!r.ok) {  continue; }
            const data = await r.json();

            const items = data.pois?.features ?? data.pois ?? [];

            all.push(...items);
        } catch (e) {

        }
    }

    return all;
}

// ── Build merged union rings from approved layers (approximate intersection) ──
// Returns a MultiPolygon GeoJSON geometry or null.
// "Intersection" is approximated by sampling ring points and keeping only those
// that fall inside ALL other layers' circles.
function buildIntersectionMultiPolygon(approvedLayers) {
    if (!approvedLayers.length) return null;
    if (approvedLayers.length === 1) {
        // Single layer — just wrap all its rings as a MultiPolygon
        const polys = approvedLayers[0].rings.map(ring => [ring]);
        return polys.length ? { type: 'MultiPolygon', coordinates: polys } : null;
    }

    // For each ring of the FIRST layer, keep only points that lie inside
    // at least one ring of EVERY other layer
    const otherLayers = approvedLayers.slice(1);
    const resultRings = [];

    approvedLayers[0].rings.forEach(ring => {
        // Filter ring points to those inside every other layer
        const filtered = ring.filter(point =>
            otherLayers.every(layer =>
                layer.rings.some(otherRing => pip(point, otherRing))
            )
        );
        // Need at least 4 points to form a polygon
        if (filtered.length >= 4) {
            const closed = [...filtered, filtered[0]];
            resultRings.push([closed]);
        }
    });

    if (!resultRings.length) return null;
    return { type: 'MultiPolygon', coordinates: resultRings };
}

// ══════════════════════════════════════════════════════════════════════════════
export default function GeoScopeAnalysis({ onBoundariesChange, initialGeoAnalysis }) {
    const [features, setFeatures] = useState([]);
    const [loadingFeatures, setLoadingFeatures] = useState(true);
    const [activeFeature, setActiveFeature] = useState(null);
    const [pois, setPOIs] = useState([]);
    const [loadingPOIs, setLoadingPOIs] = useState(false);
    const [bufferMetres, setBufferMetres] = useState(500);
    const [approvedLayers, setApprovedLayers] = useState([]);
    const [step, setStep] = useState('pick');

    // Keep callback in a ref so it never re-triggers useCallbacks below
    const onBoundariesChangeRef = useRef(onBoundariesChange);
    useEffect(() => { onBoundariesChangeRef.current = onBoundariesChange; });

    // Restore: if parent has a saved geoAnalysis, notify it immediately on mount
    // so BoundaryOverlayMap shows the geo layer without the user re-approving
    const restoredRef = useRef(false);
    useEffect(() => {
        if (restoredRef.current || !initialGeoAnalysis) return;
        restoredRef.current = true;
        if (onBoundariesChangeRef.current) onBoundariesChangeRef.current(initialGeoAnalysis);
    }, []); // eslint-disable-line

    // ── 1. Fetch geospatial features — no poi_types filter, show all GEOSPATIAL ──
    useEffect(() => {
        apiRequest(apiRoutes.geo.features, { params: { hcm_type: 'GEOSPATIAL' } })
            .then(r => r.json())
            .then(data => {
                const list = (data?.results ?? data ?? [])
                    .filter(f => f.is_active !== false)
                    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));
                setFeatures(list);
            })
            .catch(() => { })
            .finally(() => setLoadingFeatures(false));
    }, []);

    // ── 2. Load POIs when user picks a feature ────────────────────────────────
    const handlePickFeature = useCallback(async (feature) => {
        setActiveFeature(feature);
        setPOIs([]);
        setStep('configure');
        // Default buffer = feature.radius (metres) if set, else 500
        setBufferMetres(feature.radius ?? 500);

        if (feature.poi_type_ids?.length) {
            setLoadingPOIs(true);
            const items = await loadPOIs(feature.poi_type_ids);
            setPOIs(items);
            setLoadingPOIs(false);
        }
    }, []);

    const handleApprove = useCallback(() => {
        if (!activeFeature || !pois.length) return;
        const radiusKm = bufferMetres / 1000;
        const rings = pois.map(poi => {
            const c = poiCoords(poi);
            if (!c || !isFinite(c.lat) || !isFinite(c.lng)) return null;
            return createCirclePolygon({ lat: c.lat, lng: c.lng }, radiusKm, 48);
        }).filter(Boolean);
        if (!rings.length) return;
        const newLayer = { feature: activeFeature, rings, color: PALETTE[approvedLayers.length % PALETTE.length] };
        const next = [...approvedLayers, newLayer];
        setApprovedLayers(next);
        const mp = buildIntersectionMultiPolygon(next);
        if (onBoundariesChangeRef.current) onBoundariesChangeRef.current(mp);
        setActiveFeature(null);
        setPOIs([]);
        setStep('pick');
    }, [activeFeature, pois, bufferMetres, approvedLayers]);

    const handleRemoveLayer = useCallback((idx) => {
        setApprovedLayers(prev => {
            const next = prev.filter((_, i) => i !== idx);
            const mp = buildIntersectionMultiPolygon(next);
            if (onBoundariesChangeRef.current) onBoundariesChangeRef.current(mp);
            return next;
        });
    }, []);

    // ── Cancel current feature config ─────────────────────────────────────────
    const handleCancel = () => {
        setActiveFeature(null);
        setPOIs([]);
        setStep('pick');
    };

    // ── Derived ───────────────────────────────────────────────────────────────
    const alreadyApprovedIds = new Set(approvedLayers.map(l => l.feature.id));
    const availableFeatures = features.filter(f => !alreadyApprovedIds.has(f.id));
    const displayName = f => f.name_fa || f.hcm_property_request_ask || f.description || f.name;

    return (
        <div className="gsa-wrap" dir="rtl">

            {/* ── Approved layers summary ── */}
            {approvedLayers.length > 0 && (
                <div className="gsa-layers">
                    <p className="gsa-layers-title">
                        لایه‌های تأییدشده
                        {approvedLayers.length > 1 && ' — تلاقی همه لایه‌ها محدوده نهایی را می‌سازد'}
                    </p>
                    <div className="gsa-layer-list">
                        {approvedLayers.map((layer, idx) => (
                            <div key={idx} className="gsa-layer-item">
                                <span className="gsa-layer-dot" style={{ background: layer.color }} />
                                <span className="gsa-layer-name">{displayName(layer.feature)}</span>
                                <span className="gsa-layer-meta">{layer.rings.length} مکان · {layer.feature.radius ?? bufferMetres} م بافر</span>
                                <button
                                    type="button"
                                    className="gsa-layer-remove"
                                    onClick={() => handleRemoveLayer(idx)}
                                    aria-label="حذف لایه"
                                >✕</button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Step: pick ── */}
            {step === 'pick' && (
                <div className="gsa-picker">
                    <p className="gsa-picker-label">
                        {approvedLayers.length === 0
                            ? 'یک ویژگی جغرافیایی انتخاب کنید تا POI‌های مرتبط روی نقشه نمایش داده شوند:'
                            : '+ افزودن ویژگی جغرافیایی دیگر:'}
                    </p>

                    {loadingFeatures && <p className="gsa-loading">در حال بارگذاری ویژگی‌ها...</p>}

                    {!loadingFeatures && availableFeatures.length === 0 && approvedLayers.length === 0 && (
                        <p className="gsa-empty">هیچ ویژگی جغرافیایی فعالی تعریف نشده است.</p>
                    )}

                    {!loadingFeatures && availableFeatures.length === 0 && approvedLayers.length > 0 && (
                        <p className="gsa-all-done">✓ همه ویژگی‌های موجود اضافه شدند.</p>
                    )}

                    <div className="gsa-feature-grid">
                        {availableFeatures.map(f => (
                            <button
                                key={f.id}
                                type="button"
                                className="gsa-feature-chip"
                                onClick={() => handlePickFeature(f)}
                                title={f.description || f.name}
                            >
                                <span className="gsa-feature-icon">📍</span>
                                <span>{displayName(f)}</span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* ── Step: configure ── */}
            {step === 'configure' && activeFeature && (
                <div className="gsa-configure">
                    <div className="gsa-configure-header">
                        <strong className="gsa-configure-title">{displayName(activeFeature)}</strong>
                        <button type="button" className="gsa-cancel-btn" onClick={handleCancel}>انصراف</button>
                    </div>

                    {/* Buffer slider */}
                    <div className="gsa-buffer-row">
                        <label className="gsa-buffer-label">
                            شعاع دسترسی: <strong>{bufferMetres} متر</strong>
                        </label>
                        <input
                            type="range"
                            className="gsa-slider"
                            min={100}
                            max={3000}
                            step={50}
                            value={bufferMetres}
                            onChange={e => setBufferMetres(Number(e.target.value))}
                        />
                        <div className="gsa-slider-labels"><span>۱۰۰ م</span><span>۳۰۰۰ م</span></div>
                    </div>

                    {/* Map */}
                    <div className="gsa-map-wrap">
                        {loadingPOIs && (
                            <div className="gsa-map-overlay">⏳ در حال بارگذاری مکان‌ها...</div>
                        )}
                        <POIMap
                            pois={pois}
                            bufferMetres={bufferMetres}
                            approvedLayers={approvedLayers}
                        />
                    </div>

                    {/* POI count + approve */}
                    <div className="gsa-approve-row">
                        {!loadingPOIs && pois.length > 0 && (
                            <span className="gsa-poi-count">
                                📍 {pois.length} مکان پیدا شد
                                {pois.length > 300 && <span className="gsa-poi-cap"> (۳۰۰ مورد اول روی نقشه)</span>}
                            </span>
                        )}
                        {!loadingPOIs && pois.length === 0 && activeFeature.poi_type_ids?.length > 0 && (
                            <span className="gsa-poi-empty">⚠️ مکانی برای این ویژگی یافت نشد</span>
                        )}
                        <button
                            type="button"
                            className="prw-btn prw-btn-primary gsa-approve-btn"
                            onClick={handleApprove}
                            disabled={loadingPOIs || pois.length === 0}
                        >
                            ✓ تأیید و افزودن لایه
                        </button>
                    </div>
                </div>
            )}

            {/* ── Result hint ── */}
            {approvedLayers.length > 1 && step === 'pick' && (
                <div className="gsa-result-hint">
                    ✦ ناحیه تلاقی همه لایه‌ها به عنوان محدوده جستجو ذخیره خواهد شد.
                </div>
            )}
        </div>
    );
}
